import { mergeAttributes, Node } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import CitationNodeView from './CitationNodeView.vue';

export interface CitationOptions {
    HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        citation: {
            /**
             * Insert a citation node at the current position.
             */
            insertCitation: (attrs: {
                paperId: string;
                paperKey: string;
                label: string;
            }) => ReturnType;
        };
    }
}

export const CitationExtension = Node.create<CitationOptions>({
    name: 'citation',

    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,

    addOptions() {
        return { HTMLAttributes: {} };
    },

    addAttributes() {
        return {
            paperId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-paper-id'),
                renderHTML: (attrs) => ({ 'data-paper-id': attrs.paperId }),
            },
            paperKey: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-paper-key'),
                renderHTML: (attrs) => ({ 'data-paper-key': attrs.paperKey }),
            },
            label: {
                default: '',
                parseHTML: (el) => el.textContent ?? '',
                renderHTML: () => ({}),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-paper-id]' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                class: 'citation',
            }),
            node.attrs.label ?? '',
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(CitationNodeView);
    },

    addCommands() {
        return {
            insertCitation:
                (attrs) =>
                ({ chain }) => {
                    return chain()
                        .focus()
                        .insertContent({
                            type: this.name,
                            attrs,
                        })
                        .run();
                },
        };
    },
});
